import { supabase } from '@/lib/supabase';
import { BusinessError, ChannelError } from '@/features/channels/utils/errors';
import type { ChannelType } from '@/features/channels/types';
import type { PostgrestError } from '@supabase/supabase-js';

// Helper to check if error is PostgrestError
const isPostgrestError = (error: unknown): error is PostgrestError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'details' in error
  );
};

// Export the interfaces
export interface CreateChannelTypeInput {
  name: string;
  display_order: number;
  providerId: number;
  versionId: number;
  is_default?: boolean;
  is_sequence?: boolean;
}

export interface UpdateChannelTypeInput extends CreateChannelTypeInput {
  typeId: number;
  is_default?: boolean;
}

export interface DeleteChannelTypeInput {
  typeId: number;
  providerId: number;
  versionId: number;
}

// Version validation helper
const validateVersion = async (providerId: number, versionId: number) => {
  const { data, error } = await supabase
    .from('provider_versions')
    .select('status, version_number')
    .eq('provider_id', providerId)
    .eq('id', versionId)
    .single();

  if (error) {
    console.error('Version validation error:', error);
    throw new ChannelError('Invalid version configuration', 'INVALID_VERSION');
  }

  if (!data) {
    throw new ChannelError('Version not found', 'VERSION_NOT_FOUND');
  }

  if (data.status === 'published') {
    throw new ChannelError('Cannot modify channel types in published version', 'PUBLISHED_VERSION_ERROR');
  }

  return data;
};

// Add proper error codes
const handleDatabaseError = (error: PostgrestError) => {
  switch (error.code) {
    case '42703':
      throw new BusinessError('System configuration error. Please contact support.', 'CONFIG_ERROR');
    case '406':
      throw new BusinessError('Invalid version configuration', 'INVALID_VERSION');
    case '23505':
      if (error.message.includes('unique_provider_sequence_type')) {
        throw new BusinessError('Sequential provider can only have one channel type', 'SEQUENCE_TYPE_ERROR');
      }
      if (error.message.includes('unique_provider_version_type_name')) {
        throw new BusinessError('Channel type name must be unique within version', 'DUPLICATE_NAME');
      }
      throw new BusinessError('Duplicate entry detected', 'DUPLICATE_ERROR');
    default:
      throw new BusinessError(error.message, 'DATABASE_ERROR');
  }
};

export const channelTypeService = {
  getChannelTypes: async (providerId?: number, versionId?: number) => {
    if (!providerId || !versionId) return [];
    
    const { data, error } = await supabase
      .from('channel_types')
      .select('*')
      .eq('provider_id', providerId)
      .eq('version_id', versionId)
      .order('display_order');
    
    if (error) {
      console.error('Get types error:', error);
      handleDatabaseError(error as PostgrestError);
    }
    return data as ChannelType[];
  },

  getChannelType: async (typeId: number): Promise<ChannelType> => {
    const { data, error } = await supabase
      .from('channel_types')
      .select(`
        *,
        provider:providers(has_sequence_numbers)
      `)
      .eq('id', typeId)
      .single();

    if (error) throw new ChannelError('Failed to fetch channel type', 'FETCH_ERROR', error);
    return { ...data, display_name: data.name } as ChannelType;
  },

  createChannelType: async (input: CreateChannelTypeInput): Promise<ChannelType> => {
    try {
      await validateVersion(input.providerId, input.versionId);

      const { data: provider, error: providerError } = await supabase
        .from('providers')
        .select('has_sequence_numbers')
        .eq('id', input.providerId)
        .single();

      if (providerError || !provider) {
        throw new ChannelError('Failed to fetch provider', 'PROVIDER_ERROR');
      }

      const { data, error } = await supabase
        .from('channel_types')
        .insert({
          name: input.name,
          display_order: input.display_order,
          provider_id: input.providerId,
          version_id: input.versionId,
          is_sequence: input.is_sequence ?? false,
          is_default: input.is_default
        })
        .select()
        .single();

      if (error) {
        // Add better error handling
        if (isPostgrestError(error)) {
          if (error.code === '23505') {
            if (error.message.includes('unique_provider_sequence_type')) {
              throw new ChannelError('Sequential provider can only have one channel type', 'SEQUENCE_TYPE_ERROR');
            }
            throw new ChannelError('Channel type name must be unique within version', 'DUPLICATE_NAME_ERROR');
          }
        }
        throw error;
      }

      return { ...data, display_name: data.name } as ChannelType;
    } catch (error) {
      console.error('Create channel type error:', error);
      throw error instanceof ChannelError 
        ? error 
        : new ChannelError('Failed to create channel type', 'CREATE_ERROR', error as Error);
    }
  },

  updateChannelType: async (input: UpdateChannelTypeInput): Promise<ChannelType> => {
    try {
      const { data, error } = await supabase
        .rpc('update_channel_type_v2', {
          p_type_id: input.typeId,
          p_provider_id: input.providerId,
          p_version_id: input.versionId,
          p_name: input.name,
          p_display_order: input.display_order,
          p_is_default: input.is_default
        });

      if (error) {
        console.error('Update error:', error);
        if (error.message.includes('Invalid provider or version')) {
          throw new ChannelError('Invalid provider or version', 'INVALID_PROVIDER_VERSION');
        }
        if (error.message.includes('Channel type not found')) {
          throw new ChannelError('Channel type not found', 'TYPE_NOT_FOUND');

        }
        throw new ChannelError('Failed to update channel type', 'UPDATE_ERROR', error);
      }

      if (!data) {
        throw new ChannelError('Failed to update channel type', 'UPDATE_FAILED');
      }

      return { ...data, display_name: data.name } as ChannelType;
    } catch (error) {
      console.error('Update channel type error:', error);
      if (error instanceof ChannelError) {
        throw error;
      }
      throw new ChannelError('Failed to update channel type', 'UPDATE_ERROR', error as Error);
    }
  },

  deleteChannelType: async (input: DeleteChannelTypeInput): Promise<boolean> => {
    try {
      await validateVersion(input.providerId, input.versionId);

      const { data, error } = await supabase
        .rpc('delete_channel_type_v2', {
          p_type_id: input.typeId,
          p_provider_id: input.providerId
        });

      if (error) {
        if (error.message.includes('being used by channels')) {
          throw new ChannelError('Cannot delete type that is being used by channels', 'CHANNEL_TYPE_IN_USE');
        }
        throw error;
      }

      if (!data) {
        throw new ChannelError('Channel type not found', 'CHANNEL_TYPE_NOT_FOUND');
      }

      return true;
    } catch (error) {
      console.error('Delete channel type error:', error);
      throw error instanceof ChannelError 
        ? error 
        : new ChannelError('Failed to delete channel type', 'DELETE_ERROR', error as Error);
    }
  }
}; 
